import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { makeApiCall } from '../../../config/api';

const FormWizard = ({ children, formTitle, formTitleEs, formId: propFormId }) => {
  const navigate = useNavigate();
  const { formId: paramFormId } = useParams();
  const [searchParams] = useSearchParams();
  const language = searchParams.get('lang') || 'en';
  
  // Use prop formId if provided, otherwise fall back to URL param
  const formId = propFormId || paramFormId;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formType, setFormType] = useState(null);

  const t = {
    en: {
      home: 'Home',
      forms: 'Forms',
      back: 'Back',
      saveForm: 'Save Form',
      loading: 'Loading...',
      submitting: 'Submitting form...',
      alreadySubmitted: 'This form has already been submitted',
      errorLoading: 'Error loading form',
      errorSubmitting: 'Error submitting form'
    },
    es: {
      home: 'Inicio',
      forms: 'Formularios',
      back: 'Volver',
      saveForm: 'Guardar Formulario',
      loading: 'Cargando...',
      submitting: 'Enviando formulario...',
      alreadySubmitted: 'Este formulario ya ha sido enviado',
      errorLoading: 'Error al cargar el formulario',
      errorSubmitting: 'Error al enviar el formulario'
    }
  };

  const labels = t[language] || t.en;

  useEffect(() => {
    fetchFormType();
  }, [formId]);

  const fetchFormType = async () => {
    try {
      console.log('Fetching form type for ID:', formId);
      const response = await makeApiCall(`/api/employee-forms/types?language=${language}`);
      const data = await response.json();
      
      console.log('Form types response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load form type');
      }
      
      const form = data.form_types?.find(f => f.id === parseInt(formId));
      console.log('Found form:', form);
      
      if (form) {
        setFormType(form);
      } else {
        console.error('Form not found for ID:', formId);
        setFormType({ form_name: 'Form Not Implemented', form_name_es: 'Formulario No Implementado' });
      }
    } catch (err) {
      console.error('Error fetching form type:', err);
      setError(labels.errorLoading);
      setFormType({ form_name: 'Error', form_name_es: 'Error' });
    }
  };

  const handleSubmit = async (formData, signatureData) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      console.log('Submitting form:', {
        form_type_id: parseInt(formId),
        language,
        form_data: formData,
        signature_data: signatureData ? 'Present' : 'Missing'
      });

      const response = await makeApiCall('/api/employee-forms/submit', {
        method: 'POST',
        body: JSON.stringify({
          form_type_id: parseInt(formId),
          language,
          form_data: formData,
          signature_data: signatureData
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || labels.errorSubmitting);
      }

      setSuccess(data.message || (language === 'es' ? 'Formulario enviado exitosamente' : 'Form submitted successfully'));
      
      // Redirect back to forms list after 2 seconds
      setTimeout(() => {
        navigate('/forms');
      }, 2000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || labels.errorSubmitting);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/forms');
  };

  if (!formType) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {labels.loading}
        </Typography>
      </Container>
    );
  }

  const displayTitle = language === 'es' && formTitleEs ? formTitleEs : formTitle;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href="/" 
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
        >
          {labels.home}
        </Link>
        <Link 
          color="inherit" 
          href="/forms" 
          onClick={(e) => { e.preventDefault(); navigate('/forms'); }}
        >
          {labels.forms}
        </Link>
        <Typography color="text.primary">
          {displayTitle}
        </Typography>
      </Breadcrumbs>

      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {displayTitle}
              </Typography>
            </Box>
            <Button
              startIcon={<BackIcon />}
              onClick={handleBack}
              variant="outlined"
            >
              {labels.back}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>{labels.submitting}</Typography>
          </Box>
        </Alert>
      )}

      {/* Form Content */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {React.cloneElement(children, {
          language,
          onSubmit: handleSubmit,
          loading,
          disabled: loading || !!success
        })}
      </Paper>
    </Container>
  );
};

export default FormWizard;

